// Platform Admin console (PRD §5). Route-gated /padmin area for Tagnologys
// staff — its own dark shell (distinct from the green tenant app). Tenants,
// Plans and Tickets are built out; other sections show a short placeholder.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import { PADMIN_NAV, PADMIN_GROUPS } from '../mocks/padmin';
import {
  usePadminTenants, usePadminPlans, setTenantStatus, setTenantPlan, savePlan,
  type PadminPlan,
} from '../lib/padmin';
import TenantDrawer from '../components/padmin/TenantDrawer';
import TicketsPanel from '../components/padmin/TicketsPanel';
import { RevenuePanel, InvoicesPanel, CouponsPanel } from '../components/padmin/MoneyPanels';
import { HealthPanel, TemplatesPanel, BroadcastsPanel } from '../components/padmin/OpsPanels';
import OverviewPanel from '../components/padmin/OverviewPanel';
import SecurityPanel from '../components/padmin/SecurityPanel';
import { ReconcilePanel, AbusePanel, IntegrationPanel, ConsentPanel } from '../components/padmin/GrowthPanels';
import { bulkTenantAction, rowsToCsv, downloadCsv, type BulkAction } from '../lib/padminGrowth';
import { findUser, listAudit, type AuditEntry } from '../lib/padminTenant';
import { initBillingConfig, moneyOmr, omrChargeFor, usePricing } from '../lib/billing';
import { billingWebhookUrl } from '../lib/firebase';
import { useSiteContent, saveSiteContent, uploadSiteMedia, newId, DEFAULT_SITE, type SiteContent, type Bi } from '../lib/siteContent';
import { useConfirm } from '../components/ui/ConfirmDialog';

/** Promise-based single-line text prompt (replaces native window.prompt). */
interface TextPromptReq {
  title: string;
  minLength: number;
  resolve: (value: string | null) => void;
}

function TextPromptModal({ req, onDone }: { req: TextPromptReq; onDone: (value: string | null) => void }) {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const ok = value.trim().length >= req.minLength;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="presentation">
      <button type="button" aria-label={t('common.cancel')} onClick={() => onDone(null)} className="absolute inset-0 bg-slate-900/40" />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="pa-text-prompt-label"
        onSubmit={(e) => { e.preventDefault(); if (ok) onDone(value.trim()); }}
        className="relative z-10 w-full max-w-sm rounded-card border border-slate-200 bg-white p-5 shadow-lg"
      >
        <label id="pa-text-prompt-label" htmlFor="pa-text-prompt" className="block text-sm font-bold text-ink">{req.title}</label>
        <input
          id="pa-text-prompt"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mt-3 w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => onDone(null)} className="rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-bold text-ink-2 hover:bg-surface">{t('common.cancel')}</button>
          <button type="submit" disabled={!ok} className="rounded-lg bg-primary px-3.5 py-2 text-xs font-bold text-white hover:bg-primary/90 disabled:opacity-40">{t('common.confirm')}</button>
        </div>
      </form>
    </div>
  );
}

const TENANT_PILL: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  trial: 'bg-blue-100 text-blue-700',
  trialing: 'bg-blue-100 text-blue-700',
  past_due: 'bg-amber-100 text-amber-700',
  suspended: 'bg-red-100 text-red-700',
};

function fmtDate(ms: number): string {
  return ms ? new Date(ms).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
}

function UsageCell({ used, limit }: { used: number; limit: number }) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  return (
    <div className="min-w-[120px]">
      <div className="text-[11px] text-ink-2">
        {used.toLocaleString()}/{limit.toLocaleString()}
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200">
        <i
          className={cn('block h-full rounded-full', pct >= 90 ? 'bg-red-500' : 'bg-primary')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function TenantsSection() {
  const { t } = useI18n();
  const { tenants, loading, error, reload } = usePadminTenants();
  const { plans } = usePadminPlans();
  const planOptions = plans.filter((p) => !(p as { archived?: boolean }).archived);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const confirm = useConfirm();
  const [textReq, setTextReq] = useState<TextPromptReq | null>(null);
  const askText = (title: string, minLength = 1) =>
    new Promise<string | null>((resolve) => setTextReq({ title, minLength, resolve }));
  const shown = tenants.filter((x) => x.name.toLowerCase().includes(search.trim().toLowerCase()));
  const allShownSelected = shown.length > 0 && shown.every((x) => selected.has(x.id));

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAllShown() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allShownSelected) shown.forEach((x) => next.delete(x.id));
      else shown.forEach((x) => next.add(x.id));
      return next;
    });
  }

  /** Bulk apply. Export downloads a CSV; the rest mutate and report per tenant. */
  async function runBulk(action: BulkAction) {
    const ids = [...selected];
    if (ids.length === 0) return;
    // The callable caps the batch at 200 and would otherwise reject with a
    // generic validation error that tells the operator nothing.
    if (ids.length > 200) { setActionErr(t('pa.bulk.tooMany')); return; }
    let tag: string | undefined;
    let reason: string | undefined;

    if (action === 'tag' || action === 'untag') {
      const answer = await askText(t('pa.bulk.tagPrompt'));
      if (!answer) return;
      tag = answer;
    }
    if (action === 'suspend') {
      const answer = await askText(t('pa.t.suspendReason'), 3);
      if (!answer) return;
      reason = answer;
    }
    if ((action === 'suspend' || action === 'resume')
      && !(await confirm({
        title: t(`pa.bulk.${action}`),
        body: t('pa.bulk.confirm', { n: ids.length }),
        confirmLabel: t(`pa.bulk.${action}`),
        danger: action === 'suspend',
      }))) return;

    setBulkBusy(true); setActionErr(null);
    try {
      const res = await bulkTenantAction({ tenantIds: ids, action, tag, reason });
      if (action === 'export') {
        downloadCsv(`watag-tenants-${new Date().toISOString().slice(0, 10)}.csv`, rowsToCsv(res.rows));
      } else {
        reload();
        if (res.failed > 0) {
          // Keep only the failures selected so the operator can retry them
          // rather than losing the batch and having to re-find them.
          setSelected(new Set(res.results.filter((r: any) => !r.ok).map((r: any) => r.tenantId)));
        } else {
          setSelected(new Set());
        }
      }
      if (res.failed > 0) {
        const first = res.results.find((r: any) => !r.ok);
        setActionErr(`${res.failed} failed — e.g. ${first?.tenantId}: ${first?.error}`);
      }
    } catch (e) { setActionErr((e as Error).message); } finally { setBulkBusy(false); }
  }

  const stats = [
    { key: 'total', value: tenants.length },
    { key: 'active', value: tenants.filter((x) => x.status === 'active').length },
    { key: 'trial', value: tenants.filter((x) => x.status === 'trial' || x.status === 'trialing').length },
    { key: 'suspended', value: tenants.filter((x) => x.status === 'suspended').length },
  ];

  async function toggleSuspend(id: string, suspend: boolean) {
    // A suspension must carry a reason — it lands on the tenant + the audit log.
    let reason: string | undefined;
    if (suspend) {
      const answer = await askText(t('pa.t.suspendReason'), 3);
      if (!answer) return;
      reason = answer;
    }
    setBusy(id); setActionErr(null);
    try { await setTenantStatus(id, suspend, reason); reload(); }
    catch (e) { setActionErr((e as Error).message); }
    finally { setBusy(null); }
  }
  async function changePlan(id: string, planId: string) {
    setBusy(id); setActionErr(null);
    try { await setTenantPlan(id, planId); reload(); }
    catch (e) { setActionErr((e as Error).message); }
    finally { setBusy(null); }
  }

  if (error) return <div className="rounded-card border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-600">{error}</div>;

  return (
    <div>
      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.key} className="rounded-card border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-semibold text-ink-3">{t(`pa.stat.${s.key}`)}</div>
            <div className="mt-1 text-2xl font-extrabold text-ink">{s.value}</div>
          </div>
        ))}
      </div>

      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('pa.search')} aria-label={t('pa.search')} className="mb-3 w-full max-w-sm rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary" />
      {actionErr && <div className="mb-3 rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{actionErr}</div>}

      {/* Bulk action bar — only once something is selected, so it never nags. */}
      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-ink bg-ink px-4 py-2.5 text-white">
          <b className="text-sm">{t('pa.bulk.selected').replace('{n}', String(selected.size))}</b>
          {/* Selections made under a different filter are invisible in the table
              but still act — say so rather than surprising the operator. */}
          {selected.size > shown.filter((x) => selected.has(x.id)).length && (
            <span className="text-xs text-white/70">
              {t('pa.bulk.offFilter').replace('{n}',
                String(selected.size - shown.filter((x) => selected.has(x.id)).length))}
            </span>
          )}
          <div className="ms-auto flex flex-wrap gap-1.5">
            {(['tag', 'untag', 'suspend', 'resume', 'export'] as BulkAction[]).map((a) => (
              <button key={a} type="button" disabled={bulkBusy} onClick={() => void runBulk(a)}
                className={cn('rounded-[8px] px-3 py-1.5 text-xs font-bold disabled:opacity-40',
                  a === 'suspend' ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-white/15 hover:bg-white/25')}>
                {bulkBusy ? '…' : t(`pa.bulk.${a}`)}
              </button>
            ))}
            <button type="button" onClick={() => setSelected(new Set())}
              className="rounded-[8px] px-2 py-1.5 text-xs font-bold text-white/70 hover:text-white">✕</button>
          </div>
        </div>
      )}

      <div className="rounded-card border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="py-10 text-center text-sm text-ink-3">…</div>
        ) : shown.length === 0 ? (
          <div className="py-10 text-center text-sm text-ink-3">{t('pa.empty')}</div>
        ) : (
          <>
            {/* Desktop Table View (>= 768px) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse">
                <thead>
                  <tr>
                    <th className="bg-surface px-3 py-3">
                      <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown}
                        aria-label={t('pa.bulk.selectAll')} />
                    </th>
                    {['pa.t.col.name', 'pa.t.col.plan', 'pa.t.col.status', 'pa.t.col.subs', 'pa.t.col.msgs', 'pa.t.col.created', 'pa.t.col.actions'].map((k) => (
                      <th key={k} className="whitespace-nowrap bg-surface px-3.5 py-3 text-start text-[11px] font-bold uppercase tracking-wide text-ink-3">{t(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((tn) => (
                    <tr key={tn.id} className={cn('border-t border-slate-200', selected.has(tn.id) && 'bg-primary-50/40')}>
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={selected.has(tn.id)} onChange={() => toggleOne(tn.id)}
                          aria-label={tn.name} />
                      </td>
                      <td className="whitespace-nowrap px-3.5 py-3">
                        <button type="button" onClick={() => setOpenId(tn.id)}
                          className="font-bold text-ink underline decoration-slate-300 underline-offset-2 hover:text-primary hover:decoration-primary">
                          {tn.name}
                        </button>
                      </td>
                      <td className="px-3.5 py-3">
                        <select
                          value={tn.planId}
                          disabled={busy === tn.id}
                          onChange={(e) => void changePlan(tn.id, e.target.value)}
                          className="rounded-[8px] border border-slate-200 bg-white px-2 py-1 text-xs font-bold text-ink-2"
                        >
                          {/* Live catalog, so a Firestore-only or archived plan is never mislabelled. */}
                          {planOptions.map((p) => <option key={p.id} value={p.id}>{p.id}</option>)}
                          {!planOptions.some((p) => p.id === tn.planId) && <option value={tn.planId}>{tn.planId}</option>}
                        </select>
                      </td>
                      <td className="px-3.5 py-3">
                        <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-bold', TENANT_PILL[tn.status] ?? 'bg-slate-100 text-ink-2')}>{tn.status}</span>
                      </td>
                      <td className="px-3.5 py-3"><UsageCell used={tn.subscribers} limit={tn.subLimit} /></td>
                      <td className="px-3.5 py-3"><UsageCell used={tn.messagesUsed} limit={tn.msgLimit} /></td>
                      <td className="whitespace-nowrap px-3.5 py-3 text-xs text-ink-3">{fmtDate(tn.createdAt)}</td>
                      <td className="whitespace-nowrap px-3.5 py-3">
                        <button
                          type="button"
                          disabled={busy === tn.id}
                          onClick={() => void toggleSuspend(tn.id, tn.status !== 'suspended')}
                          className={cn('rounded-[8px] border bg-white px-3 py-1.5 text-xs font-bold disabled:opacity-40', tn.status === 'suspended' ? 'border-green-500 text-green-600' : 'border-red-500 text-red-500')}
                        >
                          {tn.status === 'suspended' ? t('pa.action.resume') : t('pa.action.suspend')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View (< 768px, e.g. 375px) */}
            <div className="divide-y divide-slate-200 md:hidden">
              {shown.map((tn) => (
                <div key={tn.id} className={cn('p-3.5 space-y-2.5', selected.has(tn.id) && 'bg-primary-50/30')}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <input type="checkbox" checked={selected.has(tn.id)} onChange={() => toggleOne(tn.id)}
                        aria-label={tn.name} />
                      <button type="button" onClick={() => setOpenId(tn.id)}
                        className="truncate font-bold text-sm text-ink underline decoration-slate-300 underline-offset-2 hover:text-primary">
                        {tn.name}
                      </button>
                    </div>
                    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold', TENANT_PILL[tn.status] ?? 'bg-slate-100 text-ink-2')}>
                      {tn.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] text-ink-3">{t('pa.t.col.plan')}:</span>
                      <select
                        value={tn.planId}
                        disabled={busy === tn.id}
                        onChange={(e) => void changePlan(tn.id, e.target.value)}
                        className="rounded-[6px] border border-slate-200 bg-white px-1.5 py-0.5 text-xs font-bold text-ink-2"
                      >
                        {planOptions.map((p) => <option key={p.id} value={p.id}>{p.id}</option>)}
                        {!planOptions.some((p) => p.id === tn.planId) && <option value={tn.planId}>{tn.planId}</option>}
                      </select>
                    </div>
                    <span className="text-[11px] text-ink-3">{fmtDate(tn.createdAt)}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="block text-[10px] uppercase font-bold text-ink-3">{t('pa.t.col.subs')}</span>
                      <UsageCell used={tn.subscribers} limit={tn.subLimit} />
                    </div>
                    <div>
                      <span className="block text-[10px] uppercase font-bold text-ink-3">{t('pa.t.col.msgs')}</span>
                      <UsageCell used={tn.messagesUsed} limit={tn.msgLimit} />
                    </div>
                  </div>

                  <div className="pt-1 flex justify-end">
                    <button
                      type="button"
                      disabled={busy === tn.id}
                      onClick={() => void toggleSuspend(tn.id, tn.status !== 'suspended')}
                      className={cn('rounded-[8px] border bg-white px-3 py-1 text-xs font-bold disabled:opacity-40', tn.status === 'suspended' ? 'border-green-500 text-green-600' : 'border-red-500 text-red-500')}
                    >
                      {tn.status === 'suspended' ? t('pa.action.resume') : t('pa.action.suspend')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {openId && (
        <TenantDrawer tenantId={openId} onClose={() => setOpenId(null)} onChanged={reload} />
      )}
      {textReq && (
        <TextPromptModal req={textReq} onDone={(v) => { setTextReq(null); textReq.resolve(v); }} />
      )}
    </div>
  );
}

// Global user search (feature 15) — find any user by email/phone across tenants.
function UsersSection() {
  const [q, setQ] = useState('');
  const [res, setRes] = useState<Awaited<ReturnType<typeof findUser>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function search() {
    setBusy(true); setErr(null); setRes(null);
    try { setRes(await findUser(q.trim())); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="email@example.com or +96890000000"
          aria-label="email@example.com or +96890000000"
          onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
          className="w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary" />
        <button type="button" onClick={() => void search()} disabled={busy || q.trim().length < 3}
          className="rounded-[9px] bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-40">Search</button>
      </div>
      {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
      {res && !res.user && <p className="text-sm text-ink-3">No user found.</p>}
      {res?.user && (
        <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
          <b className="block text-sm text-ink">{res.user.displayName || res.user.email}</b>
          <p className="mt-0.5 text-xs text-ink-3">{res.user.email} · <span className="font-mono">{res.user.uid}</span>{res.user.disabled ? ' · disabled' : ''}</p>
          <div className="mt-3 border-t border-slate-200 pt-3">
            <span className="text-[11px] font-bold uppercase tracking-wide text-ink-3">Tenants</span>
            {res.memberships.length === 0 ? <p className="mt-1 text-sm text-ink-3">No tenant membership.</p> : res.memberships.map((m: any) => (
              <div key={m.tenantId} className="mt-1 text-sm text-ink">
                {m.tenantName} — <b>{m.role}</b> <span className="font-mono text-[11px] text-ink-3">{m.tenantId}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Audit log (feature 49) — every privileged admin action.
function AuditSection() {
  const [rows, setRows] = useState<AuditEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { void listAudit(undefined, 100).then(setRows).catch((e) => setErr(e.message)); }, []);
  if (err) return <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>;
  if (!rows) return <div className="py-10 text-center text-sm text-ink-3">…</div>;
  if (rows.length === 0) return <p className="text-sm text-ink-3">No admin actions recorded yet.</p>;
  return (
    <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
      {rows.map((a) => (
        <div key={a.id} className="border-b border-slate-100 py-2 text-xs last:border-0">
          <b className="font-mono text-ink">{a.action}</b>
          <span className="text-ink-3"> · {new Date(a.at).toLocaleString()} · {a.actorEmail ?? 'admin'}{a.tenantId ? ` · ${a.tenantId}` : ''}</span>
          {a.reason && <div className="text-ink-2">“{a.reason}”</div>}
        </div>
      ))}
    </div>
  );
}

function PlanCard({ plan, onSaved }: { plan: PadminPlan; onSaved: () => void }) {
  const { t } = useI18n();
  const [edit, setEdit] = useState(false);
  const [p, setP] = useState<PadminPlan>(plan);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const numCls = 'w-full rounded-[8px] border border-slate-200 px-2 py-1 text-sm';

  async function doSave() {
    setBusy(true); setErr(null);
    try { await savePlan(p); setEdit(false); onSaved(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
      {err && <div className="mb-3 rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
      <div className="flex items-center justify-between">
        <b className="text-base text-ink">{p.name}</b>
        {edit ? (
          <div className="flex gap-2">
            <button type="button" onClick={() => { setP(plan); setEdit(false); }} className="rounded-[8px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-ink">{t('common.cancel')}</button>
            <button type="button" onClick={() => void doSave()} disabled={busy} className="rounded-[8px] bg-primary px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40">{busy ? '…' : t('common.apply')}</button>
          </div>
        ) : (
          <button type="button" onClick={() => setEdit(true)} className="rounded-[8px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-primary">{t('pa.plans.edit')}</button>
        )}
      </div>

      {edit ? (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {/* step=0.001: OMR is quoted to three decimals, and the default
                step of 1 makes a browser reject "1.200" as invalid. */}
            <label className="text-[11px] text-ink-3">{t('bill.per.month')} (OMR)<input type="number" step="0.001" min="0" value={p.priceMonthlyUsd} onChange={(e) => setP({ ...p, priceMonthlyUsd: Number(e.target.value) })} className={numCls} /></label>
            <label className="text-[11px] text-ink-3">{t('bill.per.year')} (OMR)<input type="number" step="0.001" min="0" value={p.priceYearlyUsd} onChange={(e) => setP({ ...p, priceYearlyUsd: Number(e.target.value) })} className={numCls} /></label>
            <label className="text-[11px] text-ink-3">{t('pa.plan.subs')}<input type="number" value={p.quotas.subscribers} onChange={(e) => setP({ ...p, quotas: { ...p.quotas, subscribers: Number(e.target.value) } })} className={numCls} /></label>
            <label className="text-[11px] text-ink-3">{t('pa.plan.msgs')}<input type="number" value={p.quotas.messagesPerMonth} onChange={(e) => setP({ ...p, quotas: { ...p.quotas, messagesPerMonth: Number(e.target.value) } })} className={numCls} /></label>
            <label className="text-[11px] text-ink-3">{t('pa.plan.seats')}<input type="number" value={p.quotas.teamSeats} onChange={(e) => setP({ ...p, quotas: { ...p.quotas, teamSeats: Number(e.target.value) } })} className={numCls} /></label>
            <label className="text-[11px] text-ink-3">{t('pa.plan.aiTokens')}<input type="number" value={p.quotas.aiTokensPerMonth ?? 0} onChange={(e) => setP({ ...p, quotas: { ...p.quotas, aiTokensPerMonth: Number(e.target.value) } })} className={numCls} /></label>
            <label className="text-[11px] text-ink-3">{t('pa.plan.numbers')}<input type="number" value={p.quotas.numbers ?? 1} onChange={(e) => setP({ ...p, quotas: { ...p.quotas, numbers: Number(e.target.value) } })} className={numCls} /></label>
            <label className="text-[11px] text-ink-3">{t('pa.plan.trialDays')}<input type="number" min="0" max="365" value={p.trialDays ?? 0} onChange={(e) => setP({ ...p, trialDays: Number(e.target.value) })} className={numCls} /></label>
          </div>
          {!p.isTrial && <CurrencyPrices plan={p} onChange={setP} />}
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-end gap-2">
            {/* Operator view: the rial figure is what Amwal actually charges,
                with the advertised USD headline beside it. */}
            <span className="text-2xl font-extrabold text-ink">{moneyOmr(p.priceMonthlyUsd)}</span>
            <span className="mb-1 text-xs text-ink-3">
              /{t('bill.per.month')} · {moneyOmr(p.priceYearlyUsd)}/{t('bill.per.year')}
            </span>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-200 pt-4 text-center sm:grid-cols-5">
            <div><dt className="text-[11px] text-ink-3">{t('pa.plan.subs')}</dt><dd className="text-sm font-bold text-ink">{p.quotas.subscribers.toLocaleString()}</dd></div>
            <div><dt className="text-[11px] text-ink-3">{t('pa.plan.msgs')}</dt><dd className="text-sm font-bold text-ink">{p.quotas.messagesPerMonth.toLocaleString()}</dd></div>
            <div><dt className="text-[11px] text-ink-3">{t('pa.plan.seats')}</dt><dd className="text-sm font-bold text-ink">{p.quotas.teamSeats}</dd></div>
            <div><dt className="text-[11px] text-ink-3">{t('pa.plan.numbers')}</dt><dd className="text-sm font-bold text-ink">{p.quotas.numbers ?? 1}</dd></div>
            <div><dt className="text-[11px] text-ink-3">{t('pa.plan.trialDays')}</dt><dd className="text-sm font-bold text-ink">{p.trialDays ?? 0}</dd></div>
          </dl>
        </>
      )}
    </div>
  );
}

/**
 * Per-currency price editor.
 *
 * The number typed here is what a customer in that market SEES. What Amwal
 * charges is derived from it via the currency's rate, so the rial is shown
 * live beside each row — an operator typing a price should be able to see
 * immediately what it will actually collect, rather than discovering it on a
 * settlement report.
 */
function CurrencyPrices({ plan, onChange }: { plan: PadminPlan; onChange: (p: PadminPlan) => void }) {
  const { t } = useI18n();
  const pricing = usePricing();
  const prices = plan.prices ?? {};
  // Offer every currency the platform knows about, not just the ones already
  // priced, so adding a market is just filling in a row.
  const codes = Array.from(new Set([...Object.keys(pricing.currencies), ...Object.keys(prices)])).sort();

  function set(code: string, field: 'monthly' | 'yearly', value: number) {
    const row = prices[code] ?? { monthly: 0, yearly: 0 };
    onChange({ ...plan, prices: { ...prices, [code]: { ...row, [field]: value } } });
  }

  return (
    <div className="mt-4 border-t border-slate-200 pt-3">
      <b className="text-[11px] font-extrabold uppercase tracking-wide text-ink-3">{t('pa.plan.pricesByCurrency')}</b>
      <p className="mt-1 text-[11px] text-ink-3">{t('pa.plan.pricesHint')}</p>
      <div className="mt-2 max-h-72 overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-white">
            <tr className="text-ink-3">
              <th className="py-1 text-start">{t('bill.currency.label')}</th>
              <th className="py-1 text-start">{t('bill.per.month')}</th>
              <th className="py-1 text-start">{t('bill.per.year')}</th>
              <th className="py-1 text-start">{t('bill.billedIn')}</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => {
              const row = prices[code];
              const monthly = Number(row?.monthly ?? 0);
              return (
                <tr key={code} className="border-t border-slate-100">
                  <td className="py-1 pe-2 font-bold text-ink">{code}</td>
                  <td className="py-1 pe-2">
                    <input type="number" step="0.001" min="0" value={row?.monthly ?? ''} placeholder="—"
                      onChange={(e) => set(code, 'monthly', Number(e.target.value))}
                      className="w-24 rounded-[6px] border border-slate-200 px-1.5 py-0.5" />
                  </td>
                  <td className="py-1 pe-2">
                    <input type="number" step="0.001" min="0" value={row?.yearly ?? ''} placeholder="—"
                      onChange={(e) => set(code, 'yearly', Number(e.target.value))}
                      className="w-24 rounded-[6px] border border-slate-200 px-1.5 py-0.5" />
                  </td>
                  <td className="py-1 text-ink-3">
                    {monthly > 0 ? moneyOmr(omrChargeFor(monthly, code, pricing.currencies)) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlansSection() {
  const { plans, loading, reload } = usePadminPlans();
  if (loading) return <div className="py-10 text-center text-sm text-ink-3">…</div>;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        {plans.map((p) => <PlanCard key={p.id} plan={p} onSaved={reload} />)}
      </div>
      <CouponsPanel />
    </div>
  );
}

function GatewaySection() {
  const { t } = useI18n();
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const res = await initBillingConfig(url.trim() || undefined);
      setSecret(res.callbackSecret);
      if (res.amwalCheckoutUrl) setUrl(res.amwalCheckoutUrl);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="max-w-2xl">
      <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
        <b className="block text-sm text-ink">{t('pa.gw.title')}</b>
        <p className="mt-1 text-xs text-ink-3">{t('pa.gw.note')}</p>
        <div className="mt-4 space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('pa.gw.url')}</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://checkout.amwalpg.com/..." aria-label={t('pa.gw.url')} className="w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary" />
          </label>
          <div>
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('pa.gw.webhook')}</span>
            <code className="block overflow-x-auto rounded-[8px] bg-surface px-2 py-1.5 font-mono text-[11px] text-ink-2">{billingWebhookUrl}</code>
          </div>
          {secret && (
            <div className="rounded-[9px] border border-primary bg-primary-50 p-2.5">
              <span className="block text-[11px] font-bold text-primary">{t('pa.gw.secret')}</span>
              <code className="mt-1 block overflow-x-auto rounded bg-white px-2 py-1 font-mono text-[11px] text-ink">{secret}</code>
            </div>
          )}
          {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
          <button type="button" onClick={() => void save()} disabled={busy} className="rounded-[9px] bg-primary px-5 py-2.5 text-[13px] font-bold text-white disabled:opacity-40">{busy ? '…' : t('pa.gw.save')}</button>
        </div>
      </div>
    </div>
  );
}

const CMS_FLD = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';
const CMS_CARD = 'rounded-card border border-slate-200 bg-white p-5 shadow-sm space-y-3';

function BiField({ label, value, onChange, textarea, rows }: { label?: string; value: Bi; onChange: (v: Bi) => void; textarea?: boolean; rows?: number }) {
  return (
    <div>
      {label ? <span className="mb-1 block text-xs font-bold text-ink-2">{label}</span> : null}
      <div className="grid grid-cols-2 gap-2">
        {(['ar', 'en'] as const).map((k) => (
          <label key={k} className="block">
            <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-ink-3">{k}</span>
            {textarea
              ? <textarea dir={k === 'ar' ? 'rtl' : 'ltr'} rows={rows ?? 2} value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className={CMS_FLD} />
              : <input dir={k === 'ar' ? 'rtl' : 'ltr'} value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className={CMS_FLD} />}
          </label>
        ))}
      </div>
    </div>
  );
}

function ContentSection() {
  const { t } = useI18n();
  const { content, loading } = useSiteContent();
  const [c, setC] = useState<SiteContent>(DEFAULT_SITE);
  const [hydrated, setHydrated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [up, setUp] = useState('');

  if (!hydrated && !loading) { setC(content); setHydrated(true); }

  async function save() {
    setBusy(true); setErr(null); setSaved(false);
    try { await saveSiteContent(c); setSaved(true); window.setTimeout(() => setSaved(false), 1800); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  async function upload(files: FileList | null, folder: 'logo' | 'video' | 'thumb', apply: (url: string) => void, key: string) {
    const f = files && files[0]; if (!f) return;
    setUp(key); setErr(null);
    try { apply(await uploadSiteMedia(f, folder)); }
    catch (e) { setErr((e as Error).message); } finally { setUp(''); }
  }
  const updVideo = (i: number, patch: Partial<SiteContent['videos'][number]>) =>
    setC((p) => ({ ...p, videos: p.videos.map((v, j) => (j === i ? { ...v, ...patch } : v)) }));
  const updDoc = (i: number, patch: Partial<SiteContent['docs'][number]>) =>
    setC((p) => ({ ...p, docs: p.docs.map((d, j) => (j === i ? { ...d, ...patch } : d)) }));

  const lbl = 'mb-1 block text-xs font-bold text-ink-2';
  const saveBtn = (
    <button type="button" onClick={() => void save()} disabled={busy}
      className="rounded-[9px] bg-primary px-5 py-2.5 text-[13px] font-bold text-white disabled:opacity-40">
      {busy ? '…' : saved ? '✓ Saved' : 'Save all'}
    </button>
  );

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div><b className="block text-sm text-ink">{t('pa.content.title')}</b><p className="mt-0.5 text-xs text-ink-3">{t('pa.content.note')}</p></div>
        {saveBtn}
      </div>
      {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}

      <div className={CMS_CARD}>
        <b className="block text-sm text-ink">Branding &amp; logo</b>
        <div className="flex items-center gap-4">
          <div className="grid h-14 w-14 place-items-center overflow-hidden rounded-[11px] border border-slate-200 bg-surface font-mono text-lg font-bold text-primary">
            {c.logoUrl ? <img src={c.logoUrl} alt="logo" className="h-full w-full object-contain" /> : 'W'}
          </div>
          <label className="cursor-pointer rounded-[9px] border border-slate-200 px-3 py-2 text-xs font-bold text-ink-2 hover:border-primary">
            {up === 'logo' ? 'Uploading…' : 'Upload logo'}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => void upload(e.currentTarget.files, 'logo', (url) => setC((p) => ({ ...p, logoUrl: url })), 'logo')} />
          </label>
          {c.logoUrl && <button type="button" onClick={() => setC((p) => ({ ...p, logoUrl: '' }))} className="text-xs font-bold text-red-500">Remove</button>}
        </div>
        <BiField label="Brand name" value={c.brandName} onChange={(v) => setC((p) => ({ ...p, brandName: v }))} />
        <BiField label="Brand subtitle" value={c.brandSub} onChange={(v) => setC((p) => ({ ...p, brandSub: v }))} />
      </div>

      <div className={CMS_CARD}>
        <b className="block text-sm text-ink">Announcement bar</b>
        <p className="text-xs text-ink-3">Leave both empty to hide the banner.</p>
        <BiField value={c.announcement} onChange={(v) => setC((p) => ({ ...p, announcement: v }))} />
      </div>

      <div className={CMS_CARD}>
        <b className="block text-sm text-ink">Hero section</b>
        <BiField label="Headline" value={c.hero.title} onChange={(v) => setC((p) => ({ ...p, hero: { ...p.hero, title: v } }))} />
        <BiField label="Subheadline" textarea rows={3} value={c.hero.sub} onChange={(v) => setC((p) => ({ ...p, hero: { ...p.hero, sub: v } }))} />
        <BiField label="Primary button" value={c.hero.ctaTrial} onChange={(v) => setC((p) => ({ ...p, hero: { ...p.hero, ctaTrial: v } }))} />
        <BiField label="Secondary button" value={c.hero.ctaDemo} onChange={(v) => setC((p) => ({ ...p, hero: { ...p.hero, ctaDemo: v } }))} />
      </div>

      <div className={CMS_CARD}>
        <b className="block text-sm text-ink">Pricing</b>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={lbl}>Pro monthly ($)</span><input value={c.proMonthly} onChange={(e) => setC((p) => ({ ...p, proMonthly: e.target.value }))} className={CMS_FLD} /></label>
          <label className="block"><span className={lbl}>Pro yearly ($)</span><input value={c.proYearly} onChange={(e) => setC((p) => ({ ...p, proYearly: e.target.value }))} className={CMS_FLD} /></label>
        </div>
      </div>

      <div className={CMS_CARD}>
        <div className="flex items-center justify-between"><b className="block text-sm text-ink">Tutorial videos</b>
          <button type="button" onClick={() => setC((p) => ({ ...p, videos: [...p.videos, { id: newId(), title: { ar: '', en: '' }, url: '', thumb: '' }] }))} className="text-xs font-bold text-primary">+ Add video</button></div>
        <p className="text-xs text-ink-3">Shown on the Academy page. Paste a YouTube / MP4 link, or upload a file.</p>
        {c.videos.length === 0 && <p className="text-xs text-ink-3">No videos yet.</p>}
        {c.videos.map((v, i) => (
          <div key={v.id} className="space-y-2 rounded-[10px] border border-slate-200 p-3">
            <div className="flex items-center justify-between"><b className="text-xs text-ink-2">Video {i + 1}</b>
              <button type="button" onClick={() => setC((p) => ({ ...p, videos: p.videos.filter((_, j) => j !== i) }))} className="text-xs font-bold text-red-500">Remove</button></div>
            <BiField label="Title" value={v.title} onChange={(nt) => updVideo(i, { title: nt })} />
            <label className="block"><span className={lbl}>Video link</span>
              <input value={v.url} onChange={(e) => updVideo(i, { url: e.target.value })} placeholder="https://youtu.be/…" className={CMS_FLD} /></label>
            <div className="flex flex-wrap items-center gap-3">
              <label className="cursor-pointer text-xs font-bold text-primary">{up === 'vid' + i ? 'Uploading…' : 'Upload video file'}
                <input type="file" accept="video/*" className="hidden" onChange={(e) => void upload(e.currentTarget.files, 'video', (url) => updVideo(i, { url }), 'vid' + i)} /></label>
              <label className="cursor-pointer text-xs font-bold text-ink-2">{up === 'thumb' + i ? 'Uploading…' : 'Upload thumbnail'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => void upload(e.currentTarget.files, 'thumb', (url) => updVideo(i, { thumb: url }), 'thumb' + i)} /></label>
              {v.thumb && <img src={v.thumb} alt="" className="h-8 rounded" />}
            </div>
          </div>
        ))}
      </div>

      <div className={CMS_CARD}>
        <div className="flex items-center justify-between"><b className="block text-sm text-ink">Learning / help docs</b>
          <button type="button" onClick={() => setC((p) => ({ ...p, docs: [...p.docs, { id: newId(), title: { ar: '', en: '' }, body: { ar: '', en: '' } }] }))} className="text-xs font-bold text-primary">+ Add doc</button></div>
        <p className="text-xs text-ink-3">Shown on the Academy page.</p>
        {c.docs.length === 0 && <p className="text-xs text-ink-3">No docs yet.</p>}
        {c.docs.map((d, i) => (
          <div key={d.id} className="space-y-2 rounded-[10px] border border-slate-200 p-3">
            <div className="flex items-center justify-between"><b className="text-xs text-ink-2">Doc {i + 1}</b>
              <button type="button" onClick={() => setC((p) => ({ ...p, docs: p.docs.filter((_, j) => j !== i) }))} className="text-xs font-bold text-red-500">Remove</button></div>
            <BiField label="Title" value={d.title} onChange={(nt) => updDoc(i, { title: nt })} />
            <BiField label="Body" textarea rows={4} value={d.body} onChange={(nb) => updDoc(i, { body: nb })} />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">{saveBtn}{saved && <span className="text-xs font-bold text-green-600">✓ Saved</span>}</div>
    </div>
  );
}

/** Billing ops = revenue + dunning, invoices, then the Amwal gateway config. */
function BillingSection({ onOpenTenant }: { onOpenTenant?: (id: string) => void }) {
  return (
    <div className="space-y-6">
      <RevenuePanel onOpenTenant={onOpenTenant} />
      <InvoicesPanel />
      <ReconcilePanel onOpenTenant={onOpenTenant} />
      <GatewaySection />
    </div>
  );
}

function Placeholder({ sectionKey }: { sectionKey: string }) {
  const { t } = useI18n();
  const item = PADMIN_NAV.find((n) => n.key === sectionKey);
  return (
    <div className="grid place-items-center rounded-card border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
      <div className="grid h-16 w-16 place-items-center rounded-2xl bg-surface text-3xl" aria-hidden>
        {item?.icon}
      </div>
      <h2 className="mt-4 text-lg font-extrabold text-ink">{t(`pa.nav.${sectionKey}`)}</h2>
      <p className="mt-1 text-sm text-ink-3">{t('pa.placeholder')}</p>
    </div>
  );
}

/** Sections with a real implementation; anything else renders a placeholder. */
const BUILT_SECTIONS = [
  'overview', 'tenants', 'users', 'plans', 'billing', 'content',
  'health', 'templates', 'broadcasts', 'integrations', 'abuse', 'compliance',
  'tickets', 'audit', 'security',
];

export default function PlatformAdmin() {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [section, setSection] = useState('overview');
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);

  /** Grouped sidebar links, shared by the desktop rail and the mobile drawer. */
  const navList = (
    <nav className="space-y-5 p-4">
      {PADMIN_GROUPS.map((group, gi) => (
        <div key={group.labelKey ?? `g${gi}`}>
          {group.labelKey && (
            <div className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-wider text-ink-3">
              {t(group.labelKey)}
            </div>
          )}
          <div className="space-y-0.5">
            {group.items.map((n) => (
              <button
                key={n.key}
                type="button"
                onClick={() => { setSection(n.key); setNavOpen(false); }}
                aria-current={section === n.key ? 'page' : undefined}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-[9px] px-3 py-2 text-start text-[13px] font-semibold transition-colors',
                  section === n.key
                    ? 'bg-ink text-white'
                    : 'text-ink-2 hover:bg-slate-100 hover:text-ink',
                )}
              >
                <span className="w-5 flex-shrink-0 text-center text-sm" aria-hidden>{n.icon}</span>
                <span className="truncate">{t(`pa.nav.${n.key}`)}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-surface">
      {/* Admin topbar (dark, distinct from tenant green) */}
      <header className="sticky top-0 z-30 flex items-center gap-3 bg-ink px-4 py-2.5 text-white sm:px-6">
        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          aria-label={t('topbar.openMenu')}
          className="grid h-8 w-8 place-items-center rounded-[9px] bg-white/15 text-base hover:bg-white/25 lg:hidden"
        >
          ☰
        </button>
        <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-[9px] bg-white/15 text-base" aria-hidden>
          🛡
        </span>
        <b className="text-sm">{t('pa.brand')}</b>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
          className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold hover:bg-white/25"
        >
          {t('lang.switch')}
        </button>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold hover:bg-white/25"
        >
          ← {t('pa.back')}
        </button>
      </header>

      <div className="flex">
        {/* Desktop sidebar: 15 sections need grouping, not one scrolling strip. */}
        <aside className="sticky top-[53px] hidden h-[calc(100vh-53px)] w-60 flex-shrink-0 overflow-y-auto border-e border-slate-200 bg-white lg:block">
          {navList}
        </aside>

        {/* Mobile drawer */}
        {navOpen && (
          <>
            <button
              type="button"
              aria-label="close menu"
              onClick={() => setNavOpen(false)}
              className="fixed inset-0 z-20 cursor-default bg-slate-900/40 lg:hidden"
            />
            <aside className="fixed inset-y-0 start-0 z-30 w-64 overflow-y-auto bg-white pt-[53px] shadow-2xl lg:hidden">
              {navList}
            </aside>
          </>
        )}

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1180px]">
            <header className="mb-6 border-b border-slate-200 pb-4">
              <h1 className="text-[24px] font-extrabold leading-tight text-ink">{t(`pa.nav.${section}`)}</h1>
              <p className="mt-1 text-sm text-ink-3">{t(`pa.desc.${section}`)}</p>
            </header>
        {section === 'overview' && <OverviewPanel onGoto={setSection} onOpenTenant={setDrawerId} />}
        {section === 'tenants' && <TenantsSection />}
        {section === 'users' && <UsersSection />}
        {section === 'plans' && <PlansSection />}
        {section === 'billing' && <BillingSection onOpenTenant={setDrawerId} />}
        {section === 'content' && <ContentSection />}
        {section === 'health' && <HealthPanel onOpenTenant={setDrawerId} />}
        {section === 'templates' && <TemplatesPanel />}
        {section === 'broadcasts' && <BroadcastsPanel />}
        {section === 'integrations' && <IntegrationPanel onOpenTenant={setDrawerId} />}
        {section === 'abuse' && <AbusePanel onOpenTenant={setDrawerId} />}
        {section === 'compliance' && <ConsentPanel />}
        {section === 'tickets' && <TicketsPanel />}
        {section === 'audit' && <AuditSection />}
            {section === 'security' && <SecurityPanel />}
            {!BUILT_SECTIONS.includes(section) && <Placeholder sectionKey={section} />}
          </div>
        </main>
      </div>

      {/* Tenant 360 opened from a cross-tenant panel (health board, dunning). */}
      {drawerId && (
        <TenantDrawer tenantId={drawerId} onClose={() => setDrawerId(null)} onChanged={() => {}} />
      )}
    </div>
  );
}
